import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { usePages } from '../hooks/usePages';
import { useTabs } from './TabsContext';
import { MAX_TABS, type LopTabLocation } from './types';
import { t } from '../i18n/t';
import { tabNeedsNewSlot, type TabMeta } from './reducer';

interface NavLocationState {
  /** Set by LopTabBar's activation navigate() — see plan §3.2. Tells
   *  TabRouterSync this REPLACE is "just focus me", never "collapse tabs". */
  lopTabActivate?: boolean;
}

function toTabLocation(loc: { pathname: string; search: string; hash: string }): LopTabLocation {
  return { pathname: loc.pathname, search: loc.search, hash: loc.hash };
}

/**
 * Bridges the real router (`useLocation` / `useNavigationType`) to the tabs
 * reducer. Mounted once, inside AppShell, OUTSIDE any per-tab
 * UNSAFE_LocationContext override — so it always sees the router's true,
 * live location, never a frozen pane's.
 *
 * PUSH   -> openOrFocus (existing key: refocus + refresh location; new: open
 *           after the active tab, D3).
 * REPLACE-> tab-bar activation (`lopTabActivate` flag) is pure focus; a
 *           genuine REPLACE (record-form ?id= assignment, a
 *           `<Navigate replace>` redirect, …) either collapses into an
 *           already-open tab for that identity or mutates the active tab in
 *           place (plan §3.2).
 * POP    -> same open-or-focus semantics as PUSH (Back/Forward to a closed
 *           tab's URL reopens it).
 * CAP    -> decision D5 (block, not overwrite): if handling the navigation
 *           would need a brand-new tab and we're already at MAX_TABS, refuse
 *           it — revert the router to the active tab's own location (the URL
 *           already changed by the time this effect runs) and report the
 *           blocked location so LopTabsCapModal opens. No tab is added,
 *           changed, or closed.
 */
export function TabRouterSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { pages } = usePages();
  const { tabs, activeKey, navOpenOrFocus, navReplace, reportCapBlocked } = useTabs();

  const pageByUrl = useMemo(() => {
    const map = new Map<string, { displayName: string; iconName: string | null }>();
    for (const p of pages ?? []) {
      if (p.url) map.set(p.url, { displayName: p.displayName, iconName: p.iconName });
    }
    return map;
  }, [pages]);

  useEffect(() => {
    const state = location.state as NavLocationState | null;
    const tabLocation = toTabLocation(location);

    const page = pageByUrl.get(tabLocation.pathname) ?? staticPageMeta(tabLocation.pathname);
    const meta: TabMeta = {
      title: buildTitle(page?.displayName ?? tabLocation.pathname, tabLocation),
      iconName: page?.iconName ?? null,
    };

    if (state?.lopTabActivate) {
      // Tab-bar click: this is exactly what stops the generic REPLACE rule
      // below from closing the tab we just navigated away from (plan
      // §3.2/§3.4). We still go through navOpenOrFocus — its "exists" branch
      // both focuses the tab AND refreshes its frozen `location` (e.g. a
      // `?search=` change applied while re-focusing) — never navReplace,
      // which would try to collapse/mutate tabs. Never cap-checked: focusing
      // an already-open tab never needs a new slot.
      navOpenOrFocus(tabLocation, meta);
      return;
    }

    // Decision D5 (block): refuse a navigation that would need a brand-new
    // tab slot while already at MAX_TABS. `tabs`/`activeKey` are read fresh
    // here (deliberately excluded from the deps array below, same reasoning
    // as `pages` — see the comment at the bottom of this effect) since this
    // effect only re-runs on a genuine `location` change, which is exactly
    // when we need their current values.
    if (tabNeedsNewSlot(tabs, activeKey, tabLocation, navigationType) && tabs.length >= MAX_TABS) {
      reportCapBlocked(tabLocation);
      const active = tabs.find((t) => t.key === activeKey);
      if (active) {
        navigate(active.location, { replace: true, state: { lopTabActivate: true } });
      }
      return;
    }

    if (navigationType === 'REPLACE') {
      navReplace(tabLocation, meta);
    } else {
      // PUSH and POP share the open-or-focus policy (plan §3.2).
      navOpenOrFocus(tabLocation, meta);
    }
    // pages is intentionally included: once GET /api/pages resolves after
    // mount, this re-runs once and upgrades placeholder pathname titles to
    // real Hebrew displayNames. `tabs`/`activeKey`/the tabs-context actions
    // are intentionally excluded — see the comment above; including `tabs`
    // would re-run this effect on every tab mutation (not just navigations)
    // and risk re-dispatching the same navigation repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigationType, pageByUrl]);

  return null;
}

// Routes that are NOT rows of the Pages table (no sidebar entry) but still need
// a proper tab title/icon. Without this the title would fall back to the raw
// pathname — and, because a REPLACE navigation within the same tab (e.g. the
// guide's ?chapter= switch) re-derives the title from meta, a page-level
// useTabTitle override would be lost.
function staticPageMeta(pathname: string): { displayName: string; iconName: string | null } | undefined {
  if (pathname === '/user-guide') return { displayName: t('userGuide.title'), iconName: 'BookOpen' };
  return undefined;
}

function buildTitle(base: string, location: LopTabLocation): string {
  const id = new URLSearchParams(location.search).get('id');
  return id !== null ? `${base} · #${id}` : base;
}
